import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Eye, MessageSquare, MapPin, ShoppingBag, PhoneCall, Truck, ShieldCheck, Users, Star, Sparkles, Award, Heart } from 'lucide-react';

/**
 * Public satisFINDER® landing page at `/satisfinder`.
 *
 * Copy is verbatim from the product brief. Do not use "VOX" anywhere on
 * this page — this is the consumer-facing satisFIND® / satisFINDER® face.
 * Captures ?ref=<referral_code> and passes it through to /satisfinder/signup.
 */
export default function SatisfinderLanding() {
  const [params] = useSearchParams();
  const ref = params.get('ref') || '';
  const signupHref = ref ? `/satisfinder/signup?ref=${encodeURIComponent(ref)}` : '/satisfinder/signup';

  return (
    <div className="min-h-screen bg-white text-[#0F172A]">
      <SFHeader />
      <Hero signupHref={signupHref} />
      <TrustLine />
      <Section1WhatSatisFindersDo />
      <Section2HowItWorks signupHref={signupHref} />
      <Section3WhatIsAFind />
      <Section4WhyJoin />
      <Section5Confidentiality />
      <Section6InviteOthers signupHref={signupHref} />
      <FinalCTA signupHref={signupHref} />
      <SFFooter />
    </div>
  );
}

function SFHeader() {
  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-[#E2E8F0]">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2" data-testid="sf-header-brand">
          <img src="/satisfind-logo.png" alt="satisFIND" style={{ height: 40, width: 'auto' }} />
        </div>
        <nav className="flex items-center gap-4 text-sm">
          <Link to="/satisfinder/login" className="text-[#334155] hover:text-[#0F172A]" data-testid="sf-header-login">
            Login
          </Link>
          <Link
            to="/satisfinder/signup"
            className="bg-[#1A3E6F] hover:bg-[#12305A] text-white font-semibold px-4 py-2 rounded-lg text-sm"
            data-testid="sf-header-signup"
          >
            Join
          </Link>
        </nav>
      </div>
    </header>
  );
}

function Hero({ signupHref }) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#0B1B2E] via-[#0F2340] to-[#0B1B2E] text-white">
      {/* decorative grain */}
      <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle at 20% 20%, #ffffff10 0%, transparent 50%), radial-gradient(circle at 80% 60%, #F59E0B15 0%, transparent 50%)',
      }} />
      <div className="relative max-w-6xl mx-auto px-6 py-24 lg:py-32">
        <p className="text-[11px] uppercase tracking-[0.24em] text-[#F59E0B] font-bold mb-4">satisFINDER® Community</p>
        <h1 className="font-['Montserrat'] text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] max-w-3xl">
          Not everyone notices the details.
        </h1>
        <p className="text-xl lg:text-2xl text-white/85 mt-6 max-w-2xl">
          Become the customer brands wish they heard from sooner.
        </p>
        <p className="text-base text-white/70 mt-6 max-w-2xl leading-relaxed">
          Join a trusted group of customers invited to observe, evaluate, and provide confidential feedback to brands that want to improve.
        </p>
        <p className="text-sm text-white/60 mt-4 max-w-2xl leading-relaxed">
          As a satisFINDER®, you may be invited to share feedback on real customer experiences across stores, restaurants, websites, calls, deliveries, and services.
        </p>
        <div className="flex flex-wrap gap-3 mt-9">
          <Link
            to={signupHref}
            className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0B1B2E] font-bold px-6 py-3 rounded-lg text-base transition-colors"
            data-testid="sf-hero-primary-cta"
          >
            Join as a satisFINDER®
          </Link>
          <a
            href="#how-it-works"
            className="border border-white/25 hover:border-white/60 text-white font-semibold px-6 py-3 rounded-lg text-base transition-colors"
            data-testid="sf-hero-secondary-cta"
          >
            See how it works
          </a>
        </div>
      </div>
    </section>
  );
}

function TrustLine() {
  return (
    <div className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
      <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-center gap-2 text-center">
        <Award className="w-4 h-4 text-[#F59E0B]" />
        <p className="text-sm text-[#475569] font-medium">
          satisFIND® has delivered customer experience insights across 55 countries.
        </p>
      </div>
    </div>
  );
}

function Section1WhatSatisFindersDo() {
  const activities = [
    { icon: MapPin,      label: 'Visit a store or branch' },
    { icon: ShoppingBag, label: 'Dine or order from a restaurant' },
    { icon: Sparkles,    label: 'Browse a website or app' },
    { icon: PhoneCall,   label: 'Call or message customer support' },
    { icon: Truck,       label: 'Check delivery or online order experiences' },
    { icon: MessageSquare, label: 'Share detailed feedback through satisFIND®' },
  ];
  return (
    <section className="max-w-6xl mx-auto px-6 py-20 lg:py-24">
      <div className="max-w-2xl">
        <p className="text-[11px] uppercase tracking-[0.24em] text-[#1A3E6F] font-bold mb-3">What satisFINDERS® do</p>
        <h2 className="font-['Montserrat'] text-3xl lg:text-4xl font-bold leading-tight">
          Help brands see what customers really experience.
        </h2>
        <p className="text-[#475569] mt-5 leading-relaxed">
          Every customer journey has details that can be missed. A greeting. A delay. A broken link. A confusing process. A moment that made the experience better or worse.
        </p>
        <p className="text-[#475569] mt-3 leading-relaxed">
          satisFINDERS® help brands understand these moments through honest, structured, and confidential feedback.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
        {activities.map(({ icon: Icon, label }, i) => (
          <div
            key={i}
            className="bg-white border border-[#E2E8F0] rounded-xl p-5 flex items-start gap-3 hover:border-[#1A3E6F]/40 hover:shadow-sm transition-all"
            data-testid={`sf-activity-${i}`}
          >
            <div className="w-10 h-10 rounded-lg bg-[#EFF6FF] text-[#1A3E6F] flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5" />
            </div>
            <p className="text-sm font-semibold text-[#0F172A] leading-snug">{label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Section2HowItWorks({ signupHref }) {
  const steps = [
    { n: 1, title: 'Join', copy: 'Create your satisFINDER® account and tell us where you are based.' },
    { n: 2, title: 'Get invited', copy: 'When a FIND matches your profile, location, and availability, you may receive an invitation.' },
    { n: 3, title: 'Complete the FIND', copy: 'Follow the brief, observe the experience, and submit your feedback through satisFIND®.' },
    { n: 4, title: 'Help brands improve', copy: 'Your confidential feedback helps brands understand what is working, what is missing, and what needs to improve.' },
  ];
  return (
    <section id="how-it-works" className="bg-[#F8FAFC] border-y border-[#E2E8F0]">
      <div className="max-w-6xl mx-auto px-6 py-20 lg:py-24">
        <p className="text-[11px] uppercase tracking-[0.24em] text-[#1A3E6F] font-bold mb-3">How it works</p>
        <h2 className="font-['Montserrat'] text-3xl lg:text-4xl font-bold leading-tight max-w-2xl">
          Four simple steps to becoming a satisFINDER®.
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {steps.map((s) => (
            <div
              key={s.n}
              className="bg-white rounded-xl border border-[#E2E8F0] p-6 flex flex-col"
              data-testid={`sf-step-${s.n}`}
            >
              <div className="w-9 h-9 rounded-full bg-[#1A3E6F] text-white flex items-center justify-center font-bold text-sm">
                {s.n}
              </div>
              <h3 className="font-['Montserrat'] font-bold text-lg mt-4">{s.title}</h3>
              <p className="text-sm text-[#475569] mt-2 leading-relaxed">{s.copy}</p>
            </div>
          ))}
        </div>
        <div className="mt-10">
          <Link
            to={signupHref}
            className="inline-block bg-[#1A3E6F] hover:bg-[#12305A] text-white font-semibold px-6 py-3 rounded-lg text-sm"
            data-testid="sf-how-it-works-cta"
          >
            Start your sign-up
          </Link>
        </div>
      </div>
    </section>
  );
}

function Section3WhatIsAFind() {
  return (
    <section className="max-w-6xl mx-auto px-6 py-20 lg:py-24">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
        <div>
          <p className="text-[11px] uppercase tracking-[0.24em] text-[#1A3E6F] font-bold mb-3">What is a FIND?</p>
          <h2 className="font-['Montserrat'] text-3xl lg:text-4xl font-bold leading-tight">What is a FIND?</h2>
          <p className="text-[#475569] mt-5 leading-relaxed">A FIND is a customer experience task.</p>
          <p className="text-[#475569] mt-3 leading-relaxed">
            Some FINDS may involve visiting a location. Others may involve placing an order, browsing a website, calling a branch, checking a delivery, or sharing feedback about a service journey.
          </p>
          <p className="text-[#475569] mt-3 leading-relaxed">
            Before accepting a FIND, you will be shown the available details, including the location, task type, timing, requirements, reward, and reimbursement where applicable.
          </p>
        </div>
        <div className="bg-gradient-to-br from-[#1A3E6F] to-[#0B1B2E] rounded-2xl p-8 text-white shadow-xl">
          <div className="flex items-center gap-2 text-[#F59E0B] mb-2">
            <Eye className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-widest font-bold">Example FIND</span>
          </div>
          <p className="text-xl font-['Montserrat'] font-bold leading-snug">
            Visit a specific branch. Order a set meal. Note staff greeting, timing, and cleanliness.
          </p>
          <div className="grid grid-cols-2 gap-4 mt-6">
            {['Location revealed', 'Task type', 'Time window', 'Reward details'].map((k) => (
              <div key={k} className="bg-white/5 border border-white/10 rounded-lg p-3">
                <p className="text-[10px] uppercase tracking-widest text-white/60">{k}</p>
                <p className="text-sm font-semibold mt-1">Shown before accepting</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Section4WhyJoin() {
  const cards = [
    { icon: Eye,         title: 'Use your eye for detail', copy: 'If you notice what others miss, your observations can help brands understand the real customer experience.' },
    { icon: MessageSquare, title: 'Give feedback that matters', copy: 'Your feedback is shared confidentially with brands that want to improve.' },
    { icon: Star,        title: 'Get invited to selected FINDS', copy: 'FIND invitations depend on your profile, location, availability, and the requirements of each project.' },
    { icon: Sparkles,    title: 'Receive rewards where applicable', copy: 'Some FINDs include rewards, reimbursements, discounts, cashback, or other benefits. Details are shown before you accept.' },
    { icon: Users,       title: 'Be part of the satisFINDER® Community', copy: 'Join customers across countries who help brands listen better and improve real experiences.' },
  ];
  return (
    <section className="bg-[#F8FAFC] border-y border-[#E2E8F0]">
      <div className="max-w-6xl mx-auto px-6 py-20 lg:py-24">
        <p className="text-[11px] uppercase tracking-[0.24em] text-[#1A3E6F] font-bold mb-3">Why become a satisFINDER®?</p>
        <h2 className="font-['Montserrat'] text-3xl lg:text-4xl font-bold leading-tight">Reasons people join.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {cards.map((c, i) => (
            <div key={i} className="bg-white border border-[#E2E8F0] rounded-xl p-6 hover:shadow-md hover:border-[#1A3E6F]/30 transition-all" data-testid={`sf-why-${i}`}>
              <div className="w-11 h-11 rounded-lg bg-[#FEF3C7] text-[#B45309] flex items-center justify-center">
                <c.icon className="w-5 h-5" />
              </div>
              <h3 className="font-['Montserrat'] font-bold text-lg mt-4">{c.title}</h3>
              <p className="text-sm text-[#475569] mt-2 leading-relaxed">{c.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Section5Confidentiality() {
  const rules = [
    'Do not share FIND details publicly.',
    'Do not reveal yourself during a FIND unless instructed.',
    'Do not contact clients directly without satisFIND® approval.',
    'Submit honest feedback based on your actual experience.',
  ];
  return (
    <section className="max-w-6xl mx-auto px-6 py-20 lg:py-24">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
        <div>
          <p className="text-[11px] uppercase tracking-[0.24em] text-[#1A3E6F] font-bold mb-3">Confidentiality</p>
          <h2 className="font-['Montserrat'] text-3xl lg:text-4xl font-bold leading-tight">Confidentiality matters.</h2>
          <p className="text-[#475569] mt-5 leading-relaxed">
            satisFINDERS® are trusted with project information, brand guidelines, survey questions, and customer experience tasks.
          </p>
          <p className="text-[#475569] mt-3 leading-relaxed">
            That is why all satisFINDERS® agree to keep FIND details confidential and follow the satisFINDER® Code of Conduct.
          </p>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-7 shadow-sm">
          <div className="flex items-center gap-2 text-[#1A3E6F]">
            <ShieldCheck className="w-5 h-5" />
            <span className="text-[11px] uppercase tracking-widest font-bold">satisFINDER® Code of Conduct</span>
          </div>
          <ul className="mt-5 space-y-3">
            {rules.map((r, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-[#334155]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] mt-2 shrink-0" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function Section6InviteOthers({ signupHref }) {
  return (
    <section className="bg-[#F8FAFC] border-y border-[#E2E8F0]">
      <div className="max-w-6xl mx-auto px-6 py-20 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] text-[#1A3E6F] font-bold mb-3">Invite others</p>
            <h2 className="font-['Montserrat'] text-3xl lg:text-4xl font-bold leading-tight">
              Know someone who notices the details?
            </h2>
            <p className="text-[#475569] mt-5 leading-relaxed">
              After joining, you can invite friends or family members who may make thoughtful satisFINDERS®.
            </p>
            <p className="text-[#475569] mt-3 leading-relaxed">
              Participation is always based on project requirements, profile match, availability, and satisFIND® approval.
            </p>
            <Link
              to={signupHref}
              className="inline-block mt-8 bg-[#1A3E6F] hover:bg-[#12305A] text-white font-semibold px-6 py-3 rounded-lg text-sm"
              data-testid="sf-invite-cta"
            >
              Invite after joining
            </Link>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-8 relative">
            <Heart className="w-8 h-8 text-[#F59E0B]" />
            <p className="font-['Montserrat'] text-2xl font-bold mt-4 leading-tight">
              Your invite matters.
            </p>
            <p className="text-sm text-[#475569] mt-3 leading-relaxed">
              Every satisFINDER® community starts with one person who noticed the details.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function FinalCTA({ signupHref }) {
  return (
    <section className="bg-[#0B1B2E] text-white">
      <div className="max-w-4xl mx-auto px-6 py-20 lg:py-24 text-center">
        <h2 className="font-['Montserrat'] text-3xl lg:text-4xl font-bold leading-tight">
          Ready to become a satisFINDER®?
        </h2>
        <p className="text-white/70 mt-4 max-w-xl mx-auto">
          Create your account in under a minute. Only 18+ can join.
        </p>
        <Link
          to={signupHref}
          className="inline-block mt-8 bg-[#F59E0B] hover:bg-[#D97706] text-[#0B1B2E] font-bold px-8 py-3.5 rounded-lg text-base"
          data-testid="sf-final-cta"
        >
          Join as a satisFINDER®
        </Link>
      </div>
    </section>
  );
}

function SFFooter() {
  const legal = [
    { label: 'Terms of Service', slug: 'terms-of-service' },
    { label: 'Privacy Policy', slug: 'privacy-policy' },
    { label: 'Refund Policy', slug: 'refund-policy' },
    { label: 'Confidentiality Agreement', slug: 'confidentiality-agreement' },
    { label: 'Code of Conduct for satisFINDERS®', slug: 'code-of-conduct-satisfinders' },
  ];
  return (
    <footer className="bg-white border-t border-[#E2E8F0]">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center">
          {legal.map((l) => (
            <Link
              key={l.slug}
              to={`/satisfinder/legal/${l.slug}`}
              className="text-xs text-[#475569] hover:text-[#1A3E6F] hover:underline"
            >
              {l.label}
            </Link>
          ))}
        </div>
        <p className="text-center text-[11px] text-[#94A3B8] mt-6 leading-relaxed">
          © 2026 SatisFIND®. All Rights Reserved.
        </p>
        <p className="text-center text-[10px] text-[#94A3B8] mt-2 max-w-3xl mx-auto leading-relaxed">
          All product and company names are trademarks or registered trademarks of their respective holders.
          Use of them does not imply any affiliation with or endorsement by them.
        </p>
      </div>
    </footer>
  );
}
